import { isEmpty } from 'lodash';
import { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { ReactElement } from 'react';

import { AccordionBlock } from '@/blocks/accordion-block';
import { HeaderBlock } from '@/blocks/header-block';
import { LinkBankBlock } from '@/blocks/link-bank-block';
import { blockTextStyles } from '@/components/block/header';
import { CountryFlag } from '@/components/country-flag';
import { Heading } from '@/components/heading';
import { PageLayout } from '@/components/layouts/page-layout';
import { Link } from '@/components/link';
import { PageRoutes } from '@/components/modules/page-routes';
import { PageLoading } from '@/components/page-loading';
import { Text } from '@/components/text';
import { PAGE_REVALIDATE_INTERVAL_MONTH, RESPONSIVE_IMAGE_WORLD_MAP } from '@/lib/constants';
import { RoutePageInterface } from '@/types';
import { cmsPostService } from '@/utils/api/services/cms.service/post';
import { routeService } from '@/utils/api/services/routes.service';
import { generateRouteFAQs } from '@/utils/frequently-asked-questions';
import { createMetaTagsWithCustomTitleAndDescription, createRoutePageUrl } from '@/utils/helpers';
import { createRouteApiSearchParams } from '@/utils/helpers/api-urls';
import { getFaqSchema } from '@/utils/helpers/json-ld';
import { findPlaceById, getPlaceAndCountryString, getPlaceCountryName } from '@/utils/places';
import { generateSeoLinkSet } from '@/utils/seo/seo-links';
import { getRoutePageMetaDescription, getRoutePageMetaTitle } from '@/utils/strings';
import { cn, tv } from '@/utils/styles';

type PageProps = { page: RoutePageInterface };

const Page = ({ page }: PageProps): ReactElement => {
  const router = useRouter();

  // If the page is not yet generated, this will be displayed
  // initially until getStaticProps() finishes running
  if (router.isFallback) {
    return <PageLoading />;
  }

  // Destructure page object
  const { from, to, fromLinks, toLinks, routes, faqs, canonical, seoMetaTags, posts } = page;

  // Generate FAQ JSONLD object
  const faqJsonLd = getFaqSchema(faqs);

  const content = {
    title: `${from.name} to ${to.name}`,
    tagLine: `By Air freight, Container ship or Road`,
    description: `See the best way to ship your freight from ${getPlaceAndCountryString(
      from,
    )} to ${getPlaceAndCountryString(
      to,
    )} by Air, Sea and Road. Compare transit times, frequency, emissions, sailing schedules and much more.`,
    image: RESPONSIVE_IMAGE_WORLD_MAP,
  };

  const countryOriginUrl = from.country?.canonical ? `/routes/${from.country.canonical}` : '/';
  const countryPairUrl =
    from.country?.canonical && to.country?.canonical
      ? `/routes/${from.country.canonical}/${to.country.canonical}`
      : '/';

  const { breadcrumb, tagline } = styles();

  return (
    <PageLayout metaTags={seoMetaTags} canonical={canonical} headerVariant="dark" posts={posts}>
      <Head>
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: `${JSON.stringify(faqJsonLd)}`,
          }}
        />
      </Head>
      <div className="blocks">
        <HeaderBlock id="header-block" variant="route" image={content.image}>
          <div className="mb-4 flex items-center">
            <Link href={countryOriginUrl} className={breadcrumb()}>
              {from.countryCode ? <CountryFlag countryCode={from.countryCode} /> : null}
              <Text variant="secondary" className="m-0 ml-2">
                {getPlaceCountryName(from) || from.name}
              </Text>
            </Link>
            <span className="mx-2">→</span>
            <Link href={countryPairUrl} className={breadcrumb()}>
              {to.countryCode ? <CountryFlag countryCode={to.countryCode} /> : null}
              <Text variant="secondary" className="m-0 ml-2">
                {getPlaceCountryName(to) || to.name}
              </Text>
            </Link>
          </div>
          <Heading variant="d2" className="m-0">
            {content.title} <small className={tagline()}>{content.tagLine}</small>
          </Heading>
          <div className="mt-8 max-w-3xl">
            <Text className={cn(blockTextStyles.richText, 'mb-0')}>{content.description}</Text>
          </div>
        </HeaderBlock>
        <PageRoutes from={from} to={to} routes={routes} />
        <AccordionBlock
          id="faqs"
          heading={`More about shipping cargo and freight from ${from.name} to ${to.name} by Air, Ocean and Road`}
          items={faqs}
          className="bg-grey-100"
        />
        {!isEmpty(fromLinks) && (
          <LinkBankBlock id="from-links" heading={`Shipping from ${from.name}`} links={{ links: fromLinks }} />
        )}
        {!isEmpty(toLinks) && (
          <LinkBankBlock id="to-links" heading={`Shipping to ${to.name}`} links={{ links: toLinks }} noTopPadding />
        )}
      </div>
    </PageLayout>
  );
};

const styles = tv({
  slots: {
    breadcrumb: 'flex items-center no-underline hover:underline',
    tagline: 'mb-0 mt-4 block text-baseLg font-medium tracking-normal',
  },
});

export const getStaticProps: GetStaticProps = async ({ params = {} }) => {
  const { origin, destination } = params;
  const origSlug = origin?.toString() || '';
  const destSlug = destination?.toString() || '';

  // Lookup Places by slug
  const from = findPlaceById(origSlug);
  const to = findPlaceById(destSlug);

  if (from && to) {
    const defaultFeatures =
      'pruneAirRoutes,pruneWaterRoutes,useImprovedWaterSearch,useScheduleCheck,pruneConverterAlternatives';
    const routeRequestParams = createRouteApiSearchParams(from, to, defaultFeatures);
    const [routes, posts] = await Promise.all([
      routeService.seoSummary(routeRequestParams),
      cmsPostService.getPostSnippetsCached(),
    ]);

    // If a route was found, continue
    if (!isEmpty(routes) && ('air' in routes || 'water' in routes)) {
      // SEO - Generate page titles, descriptions, links etc
      const canonical = createRoutePageUrl(origSlug, destSlug, true);
      const seoMetaTags = createMetaTagsWithCustomTitleAndDescription({
        title: getRoutePageMetaTitle(from, to),
        description: getRoutePageMetaDescription(from, to),
      });

      // Generate FAQs for page
      const faqs = generateRouteFAQs(routes, from, to);

      // Generate SEO links
      const fromLinks = generateSeoLinkSet(from, canonical, 'outbound');
      const toLinks = generateSeoLinkSet(to, canonical, 'inbound');

      return {
        props: {
          page: {
            from,
            to,
            fromLinks,
            toLinks,
            routes,
            faqs,
            canonical,
            seoMetaTags,
            posts,
          },
        },
        revalidate: PAGE_REVALIDATE_INTERVAL_MONTH,
      };
    }
  }

  // Else return 404
  return {
    notFound: true,
  };
};

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: [],
  fallback: true,
});

export default Page;
